import { FC, useState, useEffect, useCallback } from "react";
import CommonMenuListPage from "../../../components/CustomMenuListPage/CommonMenuListPage";
import { Box, Button, Stack, Typography, Snackbar, Alert } from "@mui/material";
import { Colors } from "../../../styles/constant";
import { MuiOtpInput } from "mui-one-time-password-input";
import "./ChangemPIN.scss";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { useLazyUpdatempinQuery } from "../GeneralAppElements_Api";
import { useAppSelector } from "../../../redux-store/CustomReduxHooks";
import CustomThreeDotsLoader from "../../../components/CustomThreeDotsLoader/CustomThreeDotsLoader";
import { useDispatch } from "react-redux";
import { setError } from "../../../redux-store/reducers/CsErrorSlice";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import { triggerCleverTapEvent } from "../../../utils/CleverTap";
import { CleverTapEventType } from "../../../utils/CleverTapConstant";
import { CT_ScreenEvntDtls } from "../../Feature_CleverTap/ScreenEvents/CleverTapEventDetails";
import { GTAScreenType, SCREEN_TYPE } from "../../../utils/constants";
import TriggerGTAEvent from "../../../utils/GTA";
import { useAppTranslation } from "../../../hooks/useAppTranslation";

interface Props {
  handleBackBtnClick: Function;
  pathname?: string;
}

const OtpInputSection: FC<{
  label: string;
  value: string;
  setValue: (val: string) => void;
}> = ({ label, value, setValue }) => (
  <Box
    textAlign="center"
    sx={{ "& > :not(:first-child)": { marginTop: "1rem" } }}
    className="change-mpin-wrapper"
  >
    <Typography variant="h6" lineHeight=".975rem" color={Colors.arsenic}>
      Enter {label} <span style={{ fontWeight: 700 }}>mPIN</span>
    </Typography>
    <MuiOtpInput
      value={value}
      onChange={(newValue) => {
        if (/^\d{0,10}$/.test(newValue)) {
          setValue(newValue);
        }
      }} // Update state correctly here
      display="flex"
      justifyContent="center"
      className="otp-input"
      sx={{ gap: "1.0625rem" }}
      length={4}
      TextFieldsProps={{
        inputProps: {
          inputMode: "numeric",
          pattern: "[0-9]*",
          type: "password",
        },
      }}
    />
  </Box>
);

const ChangemPIN: FC<Props> = ({ handleBackBtnClick, pathname }) => {
  const { t } = useAppTranslation("common");
  const [oldMpin, setOldMpin] = useState("");
  const [newMpin, setNewMpin] = useState("");
  const [confirmMpin, setConfirmMpin] = useState("");

  const [updateMpin, { data: Response, isError, isSuccess, error }] =
    useLazyUpdatempinQuery();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );
  const loginSlice = useAppSelector((state) => state.LoginSlice);
  const [loading, setLoading] = useState<boolean>(false);
  const appDispatch = useDispatch();

  const handleClevertapEvent = (type: string) => {
    triggerCleverTapEvent("serv_change_pin_cta_click", {
      ScreenType: SCREEN_TYPE.generalAppType,
      ScreenName: "Servicing Change mPIN Detail Enter Screen",
      isPrevPageNameRequired: true,
      ctaText: type,
    });
  };

  const handleGTMEvent = (type: string) => {
    TriggerGTAEvent("serv_change_pin_cta_clickt", {
      screen_type: GTAScreenType.DashboardScreenType,
      screen_name: "servicing_change_mpin_detail_enter_screen",
      CTA_text: type,
    });
  };

  const handleSubmit = useCallback(async () => {
    triggerCleverTapEvent("serv_change_pin_cta_click", {
      ScreenType: SCREEN_TYPE.DashboardScreenType,
      isPrevPageNameRequired: true,
      ScreenName: "Servicing Change mPIN Detail Enter Screen",
      ctaText: "setup_mpin",
    });
    handleClevertapEvent("Setup mPin");
    handleGTMEvent("Setup mPin");
    if (oldMpin === confirmMpin) {
      setSnackbarMessage(t("changeMpin.errorMessages.oldAndNewMatch"));
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    if (newMpin !== confirmMpin) {
      setSnackbarMessage(t("changeMpin.errorMessages.newAndConfirmMismatch"));
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }
    const params = {
      mobileNo: loginSlice.mobilenumber ?? "",
      oldMpin,
      newMpin,
    };
    try {
      setLoading(true);
      let response = await updateMpin(params);
      if (response.data?.code === API_STATUS_CODE.SUCCESS) {
        setSnackbarMessage(`${response.data.payload.responseMessage}`);
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } else if (response.data?.code === API_STATUS_CODE.BUSINESS_ERROR) {
        setSnackbarMessage(`${response.data.payload.responseMessage}`);
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      } else {
        appDispatch(
          setError({
            error: response.data,
            message: "API Error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      appDispatch(
        setError({
          error: new Error(),
          message: "API Error",
        }),
      );
      console.error("Failed to update mPIN:", err);
    }
  }, [newMpin, confirmMpin, oldMpin, loginSlice.mobilenumber, updateMpin, t]);

  const handleCloseSnackbar = useCallback(() => {
    setOpenSnackbar(false);
    if (isSuccess && Response?.code === API_STATUS_CODE.SUCCESS) {
      handleBackBtnClick(pathname);
    }
  }, [isSuccess, handleBackBtnClick, pathname]);

  useEffect(() => {
    triggerCleverTapEvent(CleverTapEventType.SCREEN_EVENT, {
      ScreenName: CT_ScreenEvntDtls.R_GAE.gaChangeMpinDetailsEnt.screenName,
      ScreenType: SCREEN_TYPE.DashboardScreenType,
    });
  }, []);

  return (
    <CommonMenuListPage
      title={t("changeMpin.titles.pageTitle")}
      handleBackBtnClick={handleBackBtnClick}
      pathname={pathname}
    >
      <Box
        textAlign={"center"}
        sx={{ "& > :not(:first-child)": { marginTop: ".5rem" } }}
      >
        <Typography variant="h2" color={Colors.arsenic}>
          {t("changeMpin.headings.updateYourMpin")}
        </Typography>
        <Typography variant="h5" fontWeight={400} color={"#77787B"}>
          {t("changeMpin.headings.securityHint")}
        </Typography>
      </Box>
      <Stack spacing={"2rem"} marginTop={"2.75rem"}>
        <OtpInputSection
          label={t("changeMpin.labels.old")}
          value={oldMpin}
          setValue={setOldMpin}
        />
        <OtpInputSection
          label={t("changeMpin.labels.new")}
          value={newMpin}
          setValue={setNewMpin}
        />
        <OtpInputSection
          label={t("changeMpin.labels.confirmNew")}
          value={confirmMpin}
          setValue={setConfirmMpin}
        />
        <Box
          textAlign={"center"}
          sx={{
            "& > :not(:first-child)": { marginTop: ".5rem" },
            boxShadow: "0rem .5rem 1rem 0rem #0000000A",
            borderRadius: "1rem",
          }}
          padding="1rem"
          marginX="1rem !important"
        >
          <Box
            display={"flex"}
            justifyContent={"center"}
            gap={".625rem"}
            alignItems={"center"}
            bgcolor={Colors.lightYellow}
            borderRadius={".5rem"}
            height={"1.5rem"}
          >
            <InfoRoundedIcon sx={{ fontSize: "1rem", color: "#F7A500" }} />
            <Typography
              fontSize={".625rem"}
              fontWeight={600}
              lineHeight={".9375rem"}
              letterSpacing={".0063rem"}
              fontFamily="Poppins, sans-serif"
              color="#D99202"
            >
              {t("changeMpin.labels.disclaimer")}
            </Typography>
          </Box>
          <Typography
            variant="h6"
            fontWeight={400}
            color={Colors.darkCharcoal}
            lineHeight="1.125rem"
            fontFamily="Poppins, sans-serif"
            textAlign="center"
          >
            {t("changeMpin.descriptions.differentFromLastThree")}
          </Typography>
        </Box>
      </Stack>

      <Box marginX="1rem" marginTop="auto" marginBottom=".75rem">
        {loading ? (
          <CustomThreeDotsLoader />
        ) : (
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={
              newMpin.length < 4 || oldMpin.length < 4 || confirmMpin.length < 4
            }
          >
            {t("changeMpin.actionButtons.setupMpin")}
          </Button>
        )}

        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ marginTop: "1.5rem" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{
              backgroundColor: "white",
              width: "90%",
              marginLeft: "1.25rem",
              marginRight: "1.25rem",
              borderEndStartRadius: "1.5rem",
              borderEndEndRadius: "1.5rem",
              padding: "0rem",
              display: "flex",
              justifyContent: "space-around",
              position: "absolute",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </CommonMenuListPage>
  );
};

export default ChangemPIN;
